import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Star, Filter, Calendar } from 'lucide-react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Navbar from "../components/Navbar";
import { Skeleton } from '../components/ui/skeleton';
import { formatDOP } from '../lib/currency';

function AssetCardSkeleton() {
  return (
    <div className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100">
      <Skeleton className="h-64 w-full rounded-none" />
      <div className="p-6 space-y-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-6 w-16" />
      </div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ZONES = [
  'Punta Cana','Bávaro','Santo Domingo','Samaná','Puerto Plata',
  'Cabarete','Sosúa','La Romana','Bayahíbe','Jarabacoa','Las Galeras','Santiago'
];

const today = new Date().toISOString().split('T')[0];

export default function SearchResults() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/categories`).then(r => r.json()).then(setCats).catch(() => {});
  }, []);

  const initFrom = searchParams.get('start_datetime')
    ? searchParams.get('start_datetime').split('T')[0]
    : '';
  const initTo = searchParams.get('start_datetime') && searchParams.get('end_hours')
    ? new Date(
        new Date(searchParams.get('start_datetime')).getTime() +
        parseInt(searchParams.get('end_hours'), 10) * 3600000
      ).toISOString().split('T')[0]
    : '';

  const [filters, setFilters] = useState({
    type: searchParams.get('type') || '',
    zone: searchParams.get('zone') || '',
    minPrice: '',
    maxPrice: '',
    sort: 'best_rated',
    dateFrom: initFrom,
    dateTo: initTo,
  });

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.type) params.append('type', filters.type);
    if (filters.zone) params.append('zone', filters.zone);
    if (filters.minPrice) params.append('min_price', filters.minPrice);
    if (filters.maxPrice) params.append('max_price', filters.maxPrice);
    if (filters.sort) params.append('sort', filters.sort);
    if (filters.dateFrom) {
      params.append('start_datetime', filters.dateFrom + 'T00:00:00');
      if (filters.dateTo && filters.dateTo > filters.dateFrom) {
        const diffH = Math.max(1, Math.round(
          (new Date(filters.dateTo) - new Date(filters.dateFrom)) / 3600000
        ));
        params.append('end_hours', diffH);
      }
    }

    setLoading(true);
    fetch(`${BACKEND_URL}/api/assets?${params}`)
      .then(res => res.json())
      .then(data => { setAssets(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filters]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl pt-8 pb-24 lg:pb-8">
        <div className="flex items-center gap-4 mb-8 overflow-x-auto hide-scrollbar pb-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full whitespace-nowrap">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">{t('search.filters')}</span>
          </div>

          <select
            value={filters.type}
            onChange={(e) => setFilters({...filters, type: e.target.value})}
            className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium"
          >
            <option value="">{t('search.allTypes')}</option>
            {cats.map(c => (
              <option key={c.type} value={c.type}>{c.name}</option>
            ))}
          </select>

          <select
            value={filters.zone}
            onChange={(e) => setFilters({...filters, zone: e.target.value})}
            className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium"
          >
            <option value="">{t('homepage.allZones')}</option>
            {ZONES.map(z => <option key={z} value={z}>{z}</option>)}
          </select>

          <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full whitespace-nowrap">
            <Calendar className="w-4 h-4 text-gray-400" />
            <label htmlFor="filter-from" className="text-xs text-gray-400">{t('homepage.fromShort')}</label>
            <input
              id="filter-from"
              type="date"
              value={filters.dateFrom}
              min={today}
              onChange={e => setFilters({...filters, dateFrom: e.target.value})}
              className="border-none outline-none text-sm text-gray-700 bg-transparent w-[120px]"
            />
          </div>

          <div className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full whitespace-nowrap">
            <Calendar className="w-4 h-4 text-gray-400" />
            <label htmlFor="filter-to" className="text-xs text-gray-400">{t('homepage.toShort')}</label>
            <input
              id="filter-to"
              type="date"
              value={filters.dateTo}
              min={filters.dateFrom || today}
              onChange={e => setFilters({...filters, dateTo: e.target.value})}
              className="border-none outline-none text-sm text-gray-700 bg-transparent w-[120px]"
            />
          </div>

          <select
            value={filters.sort}
            onChange={(e) => setFilters({...filters, sort: e.target.value})}
            className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium"
          >
            <option value="best_rated">{t('search.sortBestRated')}</option>
            <option value="lowest_price">{t('search.sortLowestPrice')}</option>
            <option value="highest_price">{t('search.sortHighestPrice')}</option>
          </select>
        </div>

        {!loading && assets.length > 0 && <p className="text-gray-600 mb-6">{t('search.resultsFound', { count: assets.length })}</p>}

        {!loading && assets.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center justify-center text-center py-16 md:py-24"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-32 h-32 rounded-full bg-gradient-to-br from-[#36C0C7]/15 to-[#134168]/15 flex items-center justify-center text-7xl mb-6 shadow-inner"
            >
              🛟
            </motion.div>
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-[#111827] mb-3">
              {t('search.emptyTitle', '¡Pronto habrá más aventuras aquí!')}
            </h3>
            <p className="text-gray-500 max-w-md mb-8 leading-relaxed">
              {t('search.emptyDesc', 'Por el momento no contamos con proveedores para esta categoría. Estamos sumando nuevos cada semana — ¡vuelve pronto!')}
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/search')}
                className="px-7 py-3 rounded-xl bg-gradient-to-r from-[#36C0C7] to-[#134168] text-white font-bold shadow-md hover:scale-105 transition-transform"
              >
                {t('search.emptyExploreAll', 'Ver todas las categorías')}
              </button>
              <Link
                to="/"
                className="px-7 py-3 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
              >
                {t('search.emptyHome', 'Volver al inicio')}
              </Link>
            </div>
          </motion.div>
        ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <AssetCardSkeleton key={i} />)
            : assets.map(asset => (
            <motion.div
              key={asset.asset_id}
              whileHover={{ y: -8 }}
              className="asset-card bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100"
            >
            <Link to={`/listing/${asset.asset_id}`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#134168] rounded-3xl">
              <div className="relative h-64">
                <img
                  src={asset.images?.[0]?.url || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                  alt={asset.name}
                  width="400"
                  height="256"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-3 py-1 bg-gray-100 rounded-full text-xs font-medium text-gray-700 capitalize">
                    {asset.type.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-gray-500">• {asset.location_zone}</span>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-2">{asset.name}</h3>
                <div className="flex items-center gap-2 mb-3">
                  {asset.total_rentals > 0 ? (
                    <>
                      <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" aria-hidden="true" />
                      <span className="font-semibold text-sm">{asset.rating}</span>
                      <span className="text-xs text-gray-500">({asset.total_rentals} {t('search.rentalsCountLabel')})</span>
                    </>
                  ) : (
                    <span className="text-xs text-[#134168] font-medium">{t('homepage.newOnPlatform')}</span>
                  )}
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-bold text-[#134168]">{formatDOP(asset.price_per_day)}</span>
                  <span className="text-sm text-gray-500">{t('homepage.perDayShort')}</span>
                </div>
              </div>
            </Link>
            </motion.div>
          ))}
        </div>
        )}

      </div>
    </div>
  );
}
